import {Box, Tooltip, Typography} from '@mui/material';
import {visuallyHidden} from '@mui/utils';
import {Info} from 'luxon';
import {formatValue} from './formatters';
import {useHeatScale} from './parts/heatScale';
import {HeatLegend} from './parts/HeatLegend';
import {useChartTheme} from './parts/useChartTheme';
import {ValueFormat} from './types';

export type HeatmapProps<Row> = {
  data: readonly Row[];
  weekday: (row: Row) => number;
  hour: (row: Row) => number;
  value: (row: Row) => number;
  format?: ValueFormat;
  tones?: readonly string[];
};

const HOURS = Array.from({length: 24}, (_, hour) => hour);

const cellKey = (weekday: number, hour: number) => `${weekday}-${hour}`;

const padHour = (hour: number) => String(hour).padStart(2, '0');

export const Heatmap = <Row,>({
  data,
  weekday,
  hour,
  value,
  format = 'count',
  tones,
}: HeatmapProps<Row>) => {
  const {tokens} = useChartTheme();
  const weekdays = Info.weekdays('short', {locale: 'en-GB'});
  const longWeekdays = Info.weekdays('long', {locale: 'en-GB'});
  const totals = new Map<string, number>();
  data.forEach(row => {
    const key = cellKey(weekday(row), hour(row));
    totals.set(key, (totals.get(key) ?? 0) + value(row));
  });
  const amountAt = (weekdayIndex: number, cellHour: number) =>
    totals.get(cellKey(weekdayIndex + 1, cellHour)) ?? 0;
  const {scale, cellColor} = useHeatScale(
    tones,
    Math.max(0, ...totals.values()),
  );

  return (
    <Box sx={{position: 'relative', width: '100%', minWidth: 0}}>
      <Box sx={visuallyHidden}>
        <table>
          <caption>Activity by weekday and hour</caption>
          <thead>
            <tr>
              <th scope="col">Weekday</th>
              {HOURS.map(cellHour => (
                <th key={cellHour} scope="col">
                  {padHour(cellHour)}:00
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {longWeekdays.map((dayName, dayIndex) => (
              <tr key={dayName}>
                <th scope="row">{dayName}</th>
                {HOURS.map(cellHour => (
                  <td key={cellHour}>
                    {formatValue(amountAt(dayIndex, cellHour), format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Box
        aria-hidden
        sx={{
          display: 'grid',
          gridTemplateColumns: '36px repeat(24, minmax(0, 1fr))',
          gap: `${tokens.heatmapCellGap}px`,
        }}
      >
        {weekdays.map((dayLabel, dayIndex) => (
          <Box key={dayLabel} sx={{display: 'contents'}}>
            <Typography
              sx={theme => ({
                ...theme.tokens.text.metadata,
                color: 'text.secondary',
                lineHeight: `${tokens.heatmapCellHeight}px`,
              })}
            >
              {dayLabel}
            </Typography>
            {HOURS.map(cellHour => {
              const amount = amountAt(dayIndex, cellHour);
              return (
                <Tooltip
                  key={cellHour}
                  disableInteractive
                  title={`${dayLabel} ${padHour(cellHour)}:00 · ${formatValue(amount, format)}`}
                >
                  <Box
                    sx={theme => ({
                      height: tokens.heatmapCellHeight,
                      borderRadius: `${theme.tokens.radius.sm}px`,
                      bgcolor: cellColor(amount),
                    })}
                  />
                </Tooltip>
              );
            })}
          </Box>
        ))}
        <Box />
        {HOURS.map(cellHour => (
          <Typography
            key={cellHour}
            sx={theme => ({
              ...theme.tokens.text.metadata,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              visibility: {
                xs: cellHour % 6 === 0 ? 'visible' : 'hidden',
                sm: cellHour % 3 === 0 ? 'visible' : 'hidden',
              },
            })}
          >
            {padHour(cellHour)}
          </Typography>
        ))}
      </Box>
      <HeatLegend scale={scale} />
    </Box>
  );
};
